// IndexedDB Storage Utility for large report files (PDF, DOCX, PPT, Excel)
// Solves browser localStorage 5MB QuotaExceededError by storing large binaries in IndexedDB

const DB_NAME = "rwire_reports_db";
const DB_VERSION = 1;
const STORE_NAME = "report_files";

function openDB() {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve(null);
      return;
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn("Could not open IndexedDB", req.error);
      resolve(null);
    };
  });
}

/**
 * Save report files object in IndexedDB across multiple lookup keys
 * @param {string} id - Report ID (e.g. ORIG-12345)
 * @param {string} title - Report title
 * @param {string} email - Customer email
 * @param {object} filesMap - Object containing format dataUrls: { pdf, docx, ppt, excel }
 */
export async function saveReportFiles(id, title, email, filesMap) {
  try {
    const db = await openDB();
    if (!db) return false;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);

      if (id) store.put(filesMap, `id_${id}`);
      if (title && email) {
        const titleEmailKey = `te_${title.toLowerCase().trim()}:::${email.toLowerCase().trim()}`;
        store.put(filesMap, titleEmailKey);
      }
      if (title) {
        store.put(filesMap, `t_${title.toLowerCase().trim()}`);
      }

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => {
        console.warn("IndexedDB transaction error", tx.error);
        resolve(false);
      };
    });
  } catch (err) {
    console.warn("Failed to save report files to IndexedDB:", err);
    return false;
  }
}

/**
 * Retrieve report files from IndexedDB by trying id, title+email, or title
 */
export async function getReportFiles(id, title, email) {
  try {
    const db = await openDB();
    if (!db) return null;

    const findByKey = (key) =>
      new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      });

    if (id) {
      const res = await findByKey(`id_${id}`);
      if (res) return res;
    }

    const cleanTitle = (title || "").toLowerCase().trim();
    const cleanEmail = (email || "").toLowerCase().trim();

    if (cleanTitle && cleanEmail) {
      const titleEmailKey = `te_${cleanTitle}:::${cleanEmail}`;
      const res = await findByKey(titleEmailKey);
      if (res) return res;
    }

    if (cleanTitle) {
      const res = await findByKey(`t_${cleanTitle}`);
      if (res) return res;
    }

    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const normTitle = norm(cleanTitle);

    // Fallback: Scan IndexedDB cursor to match by email and title
    const fallbackRes = await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.openCursor();
      let bestMatch = null;

      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (!cursor) {
          resolve(bestMatch);
          return;
        }
        const key = String(cursor.key || "");
        const val = cursor.value;

        if (cleanEmail && key.includes(cleanEmail)) {
          if (normTitle) {
            const keyTitle = key.replace(/^te_/, "").split(":::")[0];
            const normKeyTitle = norm(keyTitle);
            if (
              normKeyTitle === normTitle ||
              normKeyTitle.includes(normTitle) ||
              normTitle.includes(normKeyTitle)
            ) {
              bestMatch = val;
              resolve(val);
              return;
            }
          }
        }
        cursor.continue();
      };
      req.onerror = () => resolve(null);
    });

    if (fallbackRes) return fallbackRes;

    return null;
  } catch (err) {
    console.warn("Failed to get report files from IndexedDB:", err);
    return null;
  }
}
